import { Button } from "@/components/ui/button";
import {
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  Command,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ChevronDown, Check } from "lucide-react";
import { UIEvent, useState } from "react";

// Limit the list as long lists are slow to draw
const PAGE_SIZE = 50;

interface PartnerSelectProps {
  value: string | undefined;
  options: { id: string; label: string }[];
  onSelect: (value: string | undefined) => void;
  filter?: (value: string, search: string) => number;

  trigger: {
    className?: string;
    placeholder: string;
  };

  command: {
    searchPlaceholder: string;
    notFound: string;
    clearLabel?: string;
  };
}

export function Combobox({
  value,
  onSelect,
  options,
  trigger,
  command,
  filter,
}: Readonly<PartnerSelectProps>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const lowerSearch = search.toLowerCase();
  const filteredOptions = search
    ? options.filter((option) =>
        filter
          ? filter(option.id?.toString(), search) > 0
          : option.label?.toLowerCase().includes(lowerSearch),
      )
    : options;
  const visibleOptions = filteredOptions.slice(0, visibleCount);

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    setSearch("");
    setVisibleCount(PAGE_SIZE);
  };

  const handleSearchChange = (search: string) => {
    setSearch(search);
    setVisibleCount(PAGE_SIZE);
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const list = event.currentTarget;
    const distanceToBottom =
      list.scrollHeight - list.scrollTop - list.clientHeight;
    if (
      distanceToBottom < list.clientHeight &&
      visibleOptions.length < filteredOptions.length
    ) {
      setVisibleCount(visibleOptions.length + PAGE_SIZE);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          aria-label={trigger.placeholder}
          className={cn(
            "flex flex-row justify-between border border-input",
            trigger.className,
          )}
        >
          {options.find((option) => option.id?.toString() === value)?.label ??
            trigger.placeholder}
          <ChevronDown
            className={cn(
              "ml-2 hidden h-4 w-4 shrink-0 opacity-50 transition-transform duration-300 lg:block",
              open && "-scale-y-100",
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={command.searchPlaceholder}
            value={search}
            onValueChange={handleSearchChange}
          />
          <CommandList onScroll={handleScroll}>
            <CommandEmpty>{command.notFound}</CommandEmpty>
            <CommandGroup>
              {command.clearLabel && !search && (
                <CommandItem
                  onSelect={() => {
                    onSelect(undefined);
                    handleOpenChange(false);
                  }}
                >
                  <span className="ml-6">{command.clearLabel}</span>
                </CommandItem>
              )}
              {visibleOptions.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.id?.toString()}
                  onSelect={(value) => {
                    onSelect(value);
                    handleOpenChange(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 size-4 shrink-0",
                      value === option.id?.toString()
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  <span>{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
